// src/pages/subjects/tutor/SubjectTutorShell.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";

import "../../../styles/Subjects.css";

import learnHutLogo from "../../../Logos To be used/learnhut_logo_white_green.png";
import collapsableSettings from "../../../Logos To be used/Collapsable settings.svg";
import subjectLeftHandler from "../../../Logos To be used/subject left handler.svg";
import TutorNewIcon from "../../../Logos To be used/Dashboard Tutor New.svg";

import SubjectTutorR from "./SubjectTutorContent";
import {
  getAltKlausurenPracticeLabel,
  getDirectAltklausurenPracticeLabel,
  isDirectAltklausurenPracticeSubject,
  isKlausurPraxisSubject,
} from "../exceptions/subjectExceptions";

/* ---------------- GraphQL ---------------- */

const SUBJECTS_QUERY = gql`
  query Subjects {
    subjects {
      id
      title
      slug
      description
      isPublic
    }
  }
`;

const LESSON_CATEGORIES_BY_SUBJECT_QUERY = gql`
  query LessonCategoriesBySubject($subjectId: ID!, $mode: LessonVisibilityMode!) {
    lessonCategoriesBySubject(subjectId: $subjectId, mode: $mode) {
      id
      title
      slug
      position
      lessons(mode: $mode) {
        id
        title
        slug
        order
        isPublic
      }
    }
  }
`;

const CREATE_LESSON_CATEGORY_MUTATION = gql`
  mutation CreateLessonCategory($input: CreateLessonCategoryInput!) {
    createLessonCategory(input: $input) {
      id
      subjectId
      title
      slug
      position
    }
  }
`;

const UPDATE_LESSON_CATEGORY_MUTATION = gql`
  mutation UpdateLessonCategory($input: UpdateLessonCategoryInput!) {
    updateLessonCategory(input: $input) {
      id
      subjectId
      title
      slug
      position
    }
  }
`;

const CREATE_LESSON_MUTATION = gql`
  mutation CreateLesson($input: CreateLessonInput!) {
    createLesson(input: $input) {
      id
      subjectId
      categoryId
      title
      slug
      order
      isPublic
    }
  }
`;

const UPDATE_LESSON_MUTATION = gql`
  mutation UpdateLesson($input: UpdateLessonInput!) {
    updateLesson(input: $input) {
      id
      subjectId
      categoryId
      title
      slug
      order
      isPublic
    }
  }
`;

/* ---------------- helpers ---------------- */

const sortNum = (a, b, key) => {
  const av = typeof a?.[key] === "number" ? a[key] : 0;
  const bv = typeof b?.[key] === "number" ? b[key] : 0;
  return av - bv;
};

const clampText = (s) => (s ?? "").toString().trim();

/* ---------------- ICONS (CSS-controlled sizing) ---------------- */

const RenameIcon4 = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3 17.25V21h3.75L18.5 9.25a2.12 2.12 0 0 0 0-3L16.75 4.5a2.12 2.12 0 0 0-3 0L3 15.25Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 5.5 18.5 10.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronUpIcon4 = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 14l6-6 6 6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon4 = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 10l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* ---------------- component ---------------- */

const SubjectTutorL = () => {
  const navigate = useNavigate();
  const { subjectSlug, lessonSlug } = useParams();

  const [isFirstDropdownOpen, setFirstDropdownOpen] = useState(true);
  const [isPraxisDropdownOpen, setPraxisDropdownOpen] = useState(false);
  const [isCollapsed, setCollapsed] = useState(false);

  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedLessonTitle, setSelectedLessonTitle] = useState("");
  const [showAltKlausurenEditor, setShowAltKlausurenEditor] = useState(false);

  const [openCategoryMenuId4, setOpenCategoryMenuId4] = useState(null);
  const [openLessonMenuId4, setOpenLessonMenuId4] = useState(null);

  const [creatingCategory4, setCreatingCategory4] = useState(false);
  const [newCategoryTitle4, setNewCategoryTitle4] = useState("");

  const [renamingCategoryId4, setRenamingCategoryId4] = useState(null);
  const [renameCategoryValue4, setRenameCategoryValue4] = useState("");

  const [creatingLessonCatId4, setCreatingLessonCatId4] = useState(null);
  const [newLessonTitle4, setNewLessonTitle4] = useState("");

  const [renamingLessonId4, setRenamingLessonId4] = useState(null);
  const [renameLessonValue4, setRenameLessonValue4] = useState("");

  // close menus when clicking outside
  useEffect(() => {
    const onDown = (e) => {
      const menus = document.querySelectorAll(".tutor-menu4");
      const dots = document.querySelectorAll(".tutor-dots4.is-open4");

      const inAnyMenu = Array.from(menus).some((m) => m.contains(e.target));
      const inAnyDots = Array.from(dots).some((d) => d.contains(e.target));

      if (!inAnyMenu && !inAnyDots) {
        setOpenCategoryMenuId4(null);
        setOpenLessonMenuId4(null);
      }
    };

    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // ✅ Go back to dashboard respecting toggle mode
  const goToDashboard = () => {
    const role = localStorage.getItem("userRole");
    const isTutor = role === "TUTOR" || role === "ADMIN";

    if (!isTutor) {
      navigate("/dashboard");
      return;
    }

    const mode = localStorage.getItem("tutorViewMode");
    if (mode === "tutor") navigate("/dashboard-tutor");
    else navigate("/dashboard");
  };

  // ----- Load subject -----
  const {
    data: subjectsData,
    loading: subjectsLoading,
    error: subjectsError,
  } = useQuery(SUBJECTS_QUERY, { fetchPolicy: "network-only" });

  const subject = useMemo(() => {
    const list = subjectsData?.subjects || [];
    return list.find((s) => s?.slug === subjectSlug) || null;
  }, [subjectsData, subjectSlug]);

  const subjectId = subject?.id || null;
  const altKlausurenPracticeLabel = getAltKlausurenPracticeLabel(subjectId);
  const hasDirectAltklausurenPractice = isDirectAltklausurenPracticeSubject(subjectId);
  const directAltklausurenPracticeLabel = getDirectAltklausurenPracticeLabel(subjectId);
  const hideAiGeneratorMenuItem = isKlausurPraxisSubject(subjectId);

  // ----- Load categories -----
  const {
    data: lessonCatData,
    loading: lessonCatLoading,
    error: lessonCatError,
    refetch: refetchCats4,
  } = useQuery(LESSON_CATEGORIES_BY_SUBJECT_QUERY, {
    variables: subjectId ? { subjectId, mode: "ALL" } : undefined,
    skip: !subjectId,
    fetchPolicy: "network-only",
  });

  const [createLessonCategory4] = useMutation(CREATE_LESSON_CATEGORY_MUTATION);
  const [updateLessonCategory4] = useMutation(UPDATE_LESSON_CATEGORY_MUTATION);
  const [createLesson4] = useMutation(CREATE_LESSON_MUTATION);
  const [updateLesson4] = useMutation(UPDATE_LESSON_MUTATION);

  const [localCats4, setLocalCats4] = useState([]);
  const catRefs4 = useRef(new Map());

  const lessonRefs4 = useRef(new Map());
  const [localLessonsByCat4, setLocalLessonsByCat4] = useState({});

  const serverCats4 = useMemo(() => {
    const cats = lessonCatData?.lessonCategoriesBySubject || [];
    const sorted = [...cats].sort((a, b) => sortNum(a, b, "position"));

    return sorted.map((c) => {
      const lessons = (c.lessons || []).slice().sort((a, b) => sortNum(a, b, "order"));
      return { ...c, lessons };
    });
  }, [lessonCatData]);

  useEffect(() => {
    setLocalCats4(serverCats4);

    const map = {};
    serverCats4.forEach((c) => {
      map[c.id] = (c.lessons || []).slice().sort((a, b) => sortNum(a, b, "order"));
    });
    setLocalLessonsByCat4(map);
  }, [serverCats4]);

  const sortedLessons = useMemo(() => {
    const all = [];
    localCats4.forEach((cat) => {
      (localLessonsByCat4[cat.id] || []).forEach((l) => all.push(l));
    });
    return all.slice().sort((a, b) => sortNum(a, b, "order"));
  }, [localCats4, localLessonsByCat4]);

  // select lesson from URL
  useEffect(() => {
    if (!lessonSlug || sortedLessons.length === 0) return;

    const match = sortedLessons.find((l) => l?.slug === lessonSlug);
    if (match) {
      setShowAltKlausurenEditor(false);
      setSelectedLessonId(match.id);
      setSelectedLessonTitle(match.title);
    } else {
      setShowAltKlausurenEditor(false);
      setSelectedLessonId(null);
      setSelectedLessonTitle("");
      navigate(`/subject/${subjectSlug}/edit`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonSlug, sortedLessons, subjectSlug]);

  const handleLessonSelect = (lesson) => {
    setShowAltKlausurenEditor(false);
    setSelectedLessonId(lesson.id);
    setSelectedLessonTitle(lesson.title);
    navigate(`/subject/${subjectSlug}/edit/${lesson.slug}`);
  };

  const goToAltKlausurenEditor = () => {
    setSelectedLessonId(null);
    setSelectedLessonTitle(altKlausurenPracticeLabel);
    setShowAltKlausurenEditor(true);
    navigate(`/subject/${subjectSlug}/edit`);
  };

  /* ---------------- animations (FLIP) ---------------- */

  const animateSwap4 = (beforeRects, refsMap) => {
    requestAnimationFrame(() => {
      const afterRects = new Map();
      beforeRects.forEach((_, id) => {
        const el = refsMap.get(id);
        if (!el) return;
        afterRects.set(id, el.getBoundingClientRect());
      });

      beforeRects.forEach((beforeRect, id) => {
        const afterRect = afterRects.get(id);
        const el = refsMap.get(id);
        if (!beforeRect || !afterRect || !el) return;

        const dx = beforeRect.left - afterRect.left;
        const dy = beforeRect.top - afterRect.top;
        if (dx === 0 && dy === 0) return;

        el.style.transform = `translate(${dx}px, ${dy}px)`;
        el.style.transition = "transform 0s";
        // eslint-disable-next-line no-unused-expressions
        el.offsetHeight;

        el.style.transition = "transform 520ms cubic-bezier(0.22, 1, 0.36, 1)";
        el.style.transform = "translate(0px, 0px)";

        const cleanup = () => {
          el.style.transition = "";
          el.style.transform = "";
          el.removeEventListener("transitionend", cleanup);
        };
        el.addEventListener("transitionend", cleanup);
      });
    });
  };

  /* ---------------- category operations ---------------- */

  const commitCreateCategory4 = async () => {
    const title = clampText(newCategoryTitle4);
    setCreatingCategory4(false);
    setNewCategoryTitle4("");

    if (!title || !subjectId) return;

    const maxPos = localCats4.reduce((m, c) => {
      const p = typeof c.position === "number" ? c.position : 0;
      return Math.max(m, p);
    }, -1);
    const desiredPos = maxPos + 1;

    try {
      const res = await createLessonCategory4({
        variables: { input: { subjectId, title } },
      });

      const created = res?.data?.createLessonCategory;
      if (!created?.id) {
        await refetchCats4();
        return;
      }

      await updateLessonCategory4({
        variables: { input: { id: created.id, position: desiredPos } },
      });

      await refetchCats4();
    } catch (e) {
      await refetchCats4();
    }
  };

  const startRenameCategory4 = (cat) => {
    setOpenCategoryMenuId4(null);
    setRenamingCategoryId4(cat.id);
    setRenameCategoryValue4(cat.title || "");
  };

  const commitRenameCategory4 = async (catId) => {
    const title = clampText(renameCategoryValue4);
    setRenamingCategoryId4(null);
    setRenameCategoryValue4("");

    if (!title) return;

    try {
      await updateLessonCategory4({
        variables: { input: { id: catId, title } },
      });
      await refetchCats4();
    } catch (e) {
      await refetchCats4();
    }
  };

  const swapCategories4 = async (indexA, indexB) => {
    if (indexA < 0 || indexB < 0) return;
    if (indexA >= localCats4.length || indexB >= localCats4.length) return;

    const a = localCats4[indexA];
    const b = localCats4[indexB];
    if (!a?.id || !b?.id) return;

    const beforeRects = new Map();
    [a.id, b.id].forEach((id) => {
      const el = catRefs4.current.get(id);
      if (el) beforeRects.set(id, el.getBoundingClientRect());
    });

    const next = [...localCats4];
    next[indexA] = b;
    next[indexB] = a;
    setLocalCats4(next);

    animateSwap4(beforeRects, catRefs4.current);

    const posA = a.position;
    const posB = b.position;

    try {
      await updateLessonCategory4({ variables: { input: { id: a.id, position: posB } } });
      await updateLessonCategory4({ variables: { input: { id: b.id, position: posA } } });

      setLocalCats4((prev) => {
        const copy = prev.map((c) => {
          if (c.id === a.id) return { ...c, position: posB };
          if (c.id === b.id) return { ...c, position: posA };
          return c;
        });
        return copy.slice().sort((x, y) => sortNum(x, y, "position"));
      });
    } catch (e) {
      setLocalCats4(serverCats4);
    }
  };

  /* ---------------- lesson operations ---------------- */

  const commitCreateLesson4 = async (catId) => {
    const title = clampText(newLessonTitle4);
    setCreatingLessonCatId4(null);
    setNewLessonTitle4("");

    if (!title || !catId) return;

    const lessons = localLessonsByCat4[catId] || [];
    const maxOrder = lessons.reduce((m, l) => {
      const o = typeof l.order === "number" ? l.order : 0;
      return Math.max(m, o);
    }, -1);
    const desiredOrder = maxOrder + 1;

    try {
      await createLesson4({
        variables: { input: { categoryId: catId, title, order: desiredOrder } },
      });
      await refetchCats4();
    } catch (e) {
      await refetchCats4();
    }
  };

  const startRenameLesson4 = (lesson) => {
    setOpenLessonMenuId4(null);
    setRenamingLessonId4(lesson.id);
    setRenameLessonValue4(lesson.title || "");
  };

  const commitRenameLesson4 = async (lessonId) => {
    const title = clampText(renameLessonValue4);
    setRenamingLessonId4(null);
    setRenameLessonValue4("");

    if (!title) return;

    try {
      await updateLesson4({ variables: { input: { id: lessonId, title } } });
      await refetchCats4();
    } catch (e) {
      await refetchCats4();
    }
  };

  const swapLessons4 = async (catId, indexA, indexB) => {
    const lessons = localLessonsByCat4[catId] || [];
    if (indexA < 0 || indexB < 0) return;
    if (indexA >= lessons.length || indexB >= lessons.length) return;

    const a = lessons[indexA];
    const b = lessons[indexB];
    if (!a?.id || !b?.id) return;

    const beforeRects = new Map();
    [a.id, b.id].forEach((id) => {
      const el = lessonRefs4.current.get(id);
      if (el) beforeRects.set(id, el.getBoundingClientRect());
    });

    const nextLessons = lessons.slice();
    nextLessons[indexA] = b;
    nextLessons[indexB] = a;

    setLocalLessonsByCat4((prev) => ({ ...prev, [catId]: nextLessons }));
    animateSwap4(beforeRects, lessonRefs4.current);

    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;

    try {
      await updateLesson4({ variables: { input: { id: a.id, order: orderB } } });
      await updateLesson4({ variables: { input: { id: b.id, order: orderA } } });

      setLocalLessonsByCat4((prev) => {
        const updated = (prev[catId] || []).map((l) => {
          if (l.id === a.id) return { ...l, order: orderB };
          if (l.id === b.id) return { ...l, order: orderA };
          return l;
        });
        return {
          ...prev,
          [catId]: updated.slice().sort((x, y) => sortNum(x, y, "order")),
        };
      });
    } catch (e) {
      const fallback = {};
      serverCats4.forEach((c) => {
        fallback[c.id] = (c.lessons || []).slice().sort((x, y) => sortNum(x, y, "order"));
      });
      setLocalLessonsByCat4(fallback);
    }
  };

  const toggleLessonPublic4 = async (lesson) => {
    const nextVal = !lesson.isPublic;

    setLocalLessonsByCat4((prev) => {
      const catId = lesson.categoryId;
      const arr = (prev[catId] || []).map((l) => (l.id === lesson.id ? { ...l, isPublic: nextVal } : l));
      return { ...prev, [catId]: arr };
    });

    try {
      await updateLesson4({ variables: { input: { id: lesson.id, isPublic: nextVal } } });
      await refetchCats4();
    } catch (e) {
      await refetchCats4();
    }
  };

  /* ---------------- menus ---------------- */

  const CategoryMenu4 = ({ cat, index, isFirst, isLast }) => {
    const canUp = !isFirst;
    const canDown = !isLast;

    return (
      <div className="tutor-menu4 tutor-menu-right4">
        <button
          type="button"
          className="tutor-menu-item4"
          onClick={(e) => {
            e.stopPropagation();
            startRenameCategory4(cat);
          }}
        >
          <span className="tutor-menu-icon4 tutor-menu-icon-rename4">
            <RenameIcon4 className="tutor-svg4" />
          </span>
          Umbenennen
        </button>

        <button
          type="button"
          className={`tutor-menu-item4 ${!canUp ? "disabled4" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!canUp) return;
            swapCategories4(index, index - 1);
            setOpenCategoryMenuId4(null);
          }}
        >
          <span className={`tutor-menu-icon4 tutor-menu-icon-chevron4 ${!canUp ? "disabled4" : ""}`}>
            <ChevronUpIcon4 className="tutor-svg4" />
          </span>
          Up
        </button>

        <button
          type="button"
          className={`tutor-menu-item4 ${!canDown ? "disabled4" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!canDown) return;
            swapCategories4(index, index + 1);
            setOpenCategoryMenuId4(null);
          }}
        >
          <span className={`tutor-menu-icon4 tutor-menu-icon-chevron4 ${!canDown ? "disabled4" : ""}`}>
            <ChevronDownIcon4 className="tutor-svg4" />
          </span>
          Down
        </button>
      </div>
    );
  };

  const LessonMenu4 = ({ lesson, catId, index, isFirst, isLast }) => {
    const canUp = !isFirst;
    const canDown = !isLast;

    return (
      <div className="tutor-menu4 tutor-menu-right4">
        <button
          type="button"
          className="tutor-menu-item4"
          onClick={(e) => {
            e.stopPropagation();
            startRenameLesson4(lesson);
          }}
        >
          <span className="tutor-menu-icon4 tutor-menu-icon-rename4">
            <RenameIcon4 className="tutor-svg4" />
          </span>
          Umbenennen
        </button>

        <button
          type="button"
          className={`tutor-menu-item4 ${!canUp ? "disabled4" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!canUp) return;
            swapLessons4(catId, index, index - 1);
            setOpenLessonMenuId4(null);
          }}
        >
          <span className={`tutor-menu-icon4 tutor-menu-icon-chevron4 ${!canUp ? "disabled4" : ""}`}>
            <ChevronUpIcon4 className="tutor-svg4" />
          </span>
          Up
        </button>

        <button
          type="button"
          className={`tutor-menu-item4 ${!canDown ? "disabled4" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!canDown) return;
            swapLessons4(catId, index, index + 1);
            setOpenLessonMenuId4(null);
          }}
        >
          <span className={`tutor-menu-icon4 tutor-menu-icon-chevron4 ${!canDown ? "disabled4" : ""}`}>
            <ChevronDownIcon4 className="tutor-svg4" />
          </span>
          Down
        </button>

        <div className="tutor-menu-divider4" />

        <div className="tutor-menu-publishrow4">
          <div className="tutor-menu-publishtext4">Veröffentlicht</div>
          <button
            type="button"
            className={`tutor-toggle4 ${lesson.isPublic ? "on4" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleLessonPublic4(lesson);
            }}
            aria-label="Veröffentlicht umschalten"
          >
            <span className="tutor-toggleknob4" />
          </button>
        </div>
      </div>
    );
  };

  const renderLessonGroup = (cat, catIndex) => {
    const lessonsArr = localLessonsByCat4[cat.id] || [];

    return (
      <div key={cat.id} style={{ position: "relative", marginTop: "30px" }}>
        <div
          className={`tutor-category-wrap4 ${openCategoryMenuId4 === cat.id ? "menu-open4" : ""}`}
          ref={(el) => {
            if (el) catRefs4.current.set(cat.id, el);
          }}
        >
          {renamingCategoryId4 === cat.id ? (
            <input
              className="tutor-inline-input4 tutor-inline-input-category4"
              autoFocus
              value={renameCategoryValue4}
              onChange={(e) => setRenameCategoryValue4(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRenameCategory4(cat.id);
                if (e.key === "Escape") {
                  setRenamingCategoryId4(null);
                  setRenameCategoryValue4("");
                }
              }}
              onBlur={() => commitRenameCategory4(cat.id)}
            />
          ) : (
            <div className="big-title tutor-big-title-hover4">
              {cat.title}
              <button
                type="button"
                className={`tutor-dots4 ${openCategoryMenuId4 === cat.id ? "is-open4" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenLessonMenuId4(null);
                  setOpenCategoryMenuId4((prev) => (prev === cat.id ? null : cat.id));
                }}
                aria-label="Kategorie Optionen"
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          )}

          {openCategoryMenuId4 === cat.id && (
            <CategoryMenu4
              cat={cat}
              index={catIndex}
              isFirst={catIndex === 0}
              isLast={catIndex === localCats4.length - 1}
            />
          )}
        </div>

        <div className="dashed-line2" style={{ top: "35px" }} />

        {lessonsArr.map((lesson, idx) => {
          const selected = selectedLessonId === lesson.id;

          return (
            <div
              key={lesson.id}
              className={`tutor-lesson-row4 ${openLessonMenuId4 === lesson.id ? "menu-open4" : ""}`}
              ref={(el) => {
                if (el) lessonRefs4.current.set(lesson.id, el);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: idx === 0 ? "20px" : "15px",
                position: "relative",
              }}
            >
              <div className="progress-point" />

              {renamingLessonId4 === lesson.id ? (
                <input
                  className="tutor-inline-input4 tutor-inline-input-lesson4"
                  autoFocus
                  value={renameLessonValue4}
                  onChange={(e) => setRenameLessonValue4(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRenameLesson4(lesson.id);
                    if (e.key === "Escape") {
                      setRenamingLessonId4(null);
                      setRenameLessonValue4("");
                    }
                  }}
                  onBlur={() => commitRenameLesson4(lesson.id)}
                />
              ) : (
                <span
                  className={`lesson-title tutor-lesson-hover4 ${selected ? "selected-lesson" : ""}`}
                >
                  <Link
                    className="tutor-lesson-link4"
                    to={`/subject/${subjectSlug}/edit/${lesson.slug}`}
                    onClick={() => {
                      setShowAltKlausurenEditor(false);
                      setSelectedLessonId(lesson.id);
                      setSelectedLessonTitle(lesson.title);
                    }}
                  >
                    {lesson.title}
                  </Link>

                  <button
                    type="button"
                    className={`tutor-dots4 tutor-dots-lesson4 ${
                      openLessonMenuId4 === lesson.id ? "is-open4" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenCategoryMenuId4(null);
                      setOpenLessonMenuId4((prev) => (prev === lesson.id ? null : lesson.id));
                    }}
                    aria-label="Lektion Optionen"
                  >
                    <span />
                    <span />
                    <span />
                  </button>
                </span>
              )}

              {openLessonMenuId4 === lesson.id && (
                <LessonMenu4
                  lesson={lesson}
                  catId={cat.id}
                  index={idx}
                  isFirst={idx === 0}
                  isLast={idx === lessonsArr.length - 1}
                />
              )}
            </div>
          );
        })}

        {/* New lesson row */}
        <div
          className="tutor-lesson-row4"
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: lessonsArr.length === 0 ? "20px" : "15px",
            position: "relative",
          }}
        >
          <div className="progress-point" />
          <span className="lesson-title tutor-lesson-hover4 tutor-new-lesson4">
            {creatingLessonCatId4 === cat.id ? (
              <input
                className="tutor-inline-input4 tutor-inline-input-lesson4"
                autoFocus
                value={newLessonTitle4}
                onChange={(e) => setNewLessonTitle4(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitCreateLesson4(cat.id);
                  if (e.key === "Escape") {
                    setCreatingLessonCatId4(null);
                    setNewLessonTitle4("");
                  }
                }}
                onBlur={() => commitCreateLesson4(cat.id)}
              />
            ) : (
              <>
                <span
                  onClick={() => {
                    setCreatingLessonCatId4(cat.id);
                    setNewLessonTitle4("");
                    setOpenCategoryMenuId4(null);
                    setOpenLessonMenuId4(null);
                  }}
                >
                  Neue Lektion
                </span>

                <img
                  src={TutorNewIcon}
                  alt="Neu"
                  className="tutor-plus-lesson4"
                  draggable="false"
                  onClick={() => {
                    setCreatingLessonCatId4(cat.id);
                    setNewLessonTitle4("");
                    setOpenCategoryMenuId4(null);
                    setOpenLessonMenuId4(null);
                  }}
                />
              </>
            )}
          </span>
        </div>
      </div>
    );
  };

  if (subjectsLoading) return <div style={{ padding: 20 }}>Loading...</div>;
  if (subjectsError) return <div style={{ padding: 20 }}>Error loading subject.</div>;
  if (!subject) return <div style={{ padding: 20 }}>Subject not found.</div>;

  const leftWidth = isCollapsed ? 65 : 300;

  return (
    <div className={`subject-page tutor-subject4 ${isCollapsed ? "collapsed" : ""}`}>
      {/* LEFT */}
      <div className={`left-section ${isCollapsed ? "collapsed" : ""}`}>
        <div className="top-section" onClick={goToDashboard}>
          <img
            src={learnHutLogo}
            alt="LearnHut Logo"
            className="tempo-logo"
            style={{ cursor: "pointer" }}
          />
          {!isCollapsed && (
            <>
              <div className="vertical-line" />
              <span className="academy-name-wrap" style={{ cursor: "pointer" }}>
                <span className="academy-beta">BETA</span>
              </span>
            </>
          )}
        </div>

        {!isCollapsed && <hr className="separator-line" />}

        {!isCollapsed && (
          <div className="middle-section">
            <h2
              className="subject-title2"
            >
              <Link
                className="subject-title-link2"
                to={`/subject/${subjectSlug}/edit`}
                onClick={() => {
                  setSelectedLessonId(null);
                  setSelectedLessonTitle("");
                  setShowAltKlausurenEditor(false);
                }}
              >
                {subject.title}
              </Link>
            </h2>

            {hasDirectAltklausurenPractice ? (
              <button
                type="button"
                className={`dropdown-section dropdown-section-button direct-practice-section ${
                  showAltKlausurenEditor ? "active" : ""
                }`}
                onClick={goToAltKlausurenEditor}
              >
                <span className="dropdown-direct-mark" aria-hidden="true" />
                <span className="dropdown-text">{directAltklausurenPracticeLabel}</span>
              </button>
            ) : null}

            <div className="dropdown-section" onClick={() => setFirstDropdownOpen((o) => !o)}>
              <img
                src={subjectLeftHandler}
                alt="Toggle"
                className={`dropdown-icon ${isFirstDropdownOpen ? "rotated" : ""}`}
              />
              <span className="dropdown-text">
                {hasDirectAltklausurenPractice ? "Kurs & Übungen" : "Kurs und Quizfragen"}
              </span>
            </div>

            {isFirstDropdownOpen && (
              <div className="dropdown-content">
                {lessonCatLoading && (
                  <div style={{ color: "white", opacity: 0.8, marginBottom: 10 }}>Loading…</div>
                )}

                {lessonCatError && (
                  <div style={{ color: "white", opacity: 0.8, marginBottom: 10 }}>Fehler beim Laden.</div>
                )}

                {!lessonCatLoading && localCats4.map((cat, idx) => renderLessonGroup(cat, idx))}

                {!lessonCatLoading && (
                  <div style={{ marginTop: "28px" }}>
                    {creatingCategory4 ? (
                      <div className="tutor-new-category-shell4">
                        <input
                          className="tutor-inline-input4 tutor-inline-input-category4 tutor-new-category-input4"
                          autoFocus
                          value={newCategoryTitle4}
                          onChange={(e) => setNewCategoryTitle4(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitCreateCategory4();
                            if (e.key === "Escape") {
                              setCreatingCategory4(false);
                              setNewCategoryTitle4("");
                            }
                          }}
                          onBlur={() => commitCreateCategory4()}
                        />
                      </div>
                    ) : (
                      <div
                        className="tutor-new-category4"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setCreatingCategory4(true);
                          setNewCategoryTitle4("");
                          setOpenCategoryMenuId4(null);
                          setOpenLessonMenuId4(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            setCreatingCategory4(true);
                            setNewCategoryTitle4("");
                          }
                        }}
                      >
                        <span className="tutor-new-category-text4">Neues Thema</span>
                        <img src={TutorNewIcon} alt="Neu" className="tutor-plus-category4" draggable="false" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!hasDirectAltklausurenPractice ? (
              <>
                <div
                  className="dropdown-section"
                  onClick={() => setPraxisDropdownOpen((o) => !o)}
                >
                  <img
                    src={subjectLeftHandler}
                    alt="Toggle"
                    className={`dropdown-icon ${isPraxisDropdownOpen ? "rotated" : ""}`}
                  />
                  <span className="dropdown-text">Praxis-Modus</span>
                </div>

                {isPraxisDropdownOpen && (
                  <div className="dropdown-subcontent">
                    <button
                      type="button"
                      className={`dropdown-subitem ${showAltKlausurenEditor ? "active" : ""}`}
                      onClick={goToAltKlausurenEditor}
                    >
                      <span className="dropdown-subbullet" aria-hidden="true">
                        •
                      </span>
                      <span>{altKlausurenPracticeLabel}</span>
                    </button>

                    {!hideAiGeneratorMenuItem && (
                      <div
                        className="dropdown-subitem locked with-tooltip"
                        aria-disabled="true"
                        tabIndex={0}
                      >
                        <span className="dropdown-subbullet" aria-hidden="true">
                          •
                        </span>
                        <span className="dropdown-sublabel">Übungen Generator AI</span>
                        <span className="dropdown-subtooltip">Kommt später</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}

        <div className="bottom-section">
          <hr className="separator-line bottom-separator" />
          <img
            src={collapsableSettings}
            alt="Collapse"
            className={`collapse-icon ${isCollapsed ? "rotated" : ""}`}
            onClick={() => setCollapsed((c) => !c)}
          />
        </div>
      </div>

      {/* RIGHT */}
      <div className="right-section">
        <SubjectTutorR
          subjectId={subjectId}
          lessonId={selectedLessonId}
          lessonTitle={selectedLessonTitle}
          leftWidth={leftWidth}
          showAltKlausurenEditor={showAltKlausurenEditor}
          lessons={sortedLessons}
          onLessonSelect={(id) => {
            const l = sortedLessons.find((x) => x.id === id);
            if (!l) return;
            handleLessonSelect(l);
          }}
        />
      </div>
    </div>
  );
};

export default SubjectTutorL;
